import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Space, Statistic, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import AnalyticsFilters from '../components/AnalyticsFilters';
import type { AnalyticsEvent, AnalyticsSummaryItem } from '../types';

const { Title, Text } = Typography;

type DailyChartItem = {
  date: string;
  label: string;
  count: number;
};

function formatChartDate(date: string) {
  const parsed = new Date(`${date}T00:00:00`);
  return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
}

function getLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildLast30EventDays(events: AnalyticsEvent[]): DailyChartItem[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = getLocalDateString(today);
  const counts = new Map<string, number>();
  for (let index = 29; index >= 0; index -= 1) {
    const date = new Date(today);
    date.setDate(date.getDate() - index);
    counts.set(getLocalDateString(date), 0);
  }
  for (const event of events) {
    const timestamp = event.occurredAt || event.createdAt;
    if (!timestamp) continue;
    const key = getLocalDateString(new Date(timestamp));
    if (counts.has(key)) {
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  return Array.from(counts.entries()).map(([date, count]) => ({
    date,
    count,
    label: date === todayKey ? 'Today' : formatChartDate(date),
  }));
}

function ColumnChart({ title, items }: { title: string; items: DailyChartItem[] }) {
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <Card>
      <Title level={4}>{title}</Title>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200 }}>
        {items.map((item) => {
          const height = Math.max((item.count / max) * 180, 2);
          return (
            <div key={item.date} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div
                title={`${item.date}: ${item.count}`}
                style={{
                  height,
                  background: 'linear-gradient(180deg, #f46524 0%, #f08f4b 100%)',
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                }}
              >
                {item.count > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -18,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#f46524',
                    }}
                  >
                    {item.count}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [apps, setApps] = useState<AnalyticsSummaryItem[]>([]);
  const [chartEvents, setChartEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    refreshAnalytics();
  }, [ignoreAnnea, selectedOrgId, selectedUserId]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => {
      refreshAnalytics();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, ignoreAnnea, selectedOrgId, selectedUserId]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const scope = selectedUserId ? 'user' : selectedOrgId ? 'org' : 'global';
      const scopeId = selectedUserId || selectedOrgId || undefined;
      const data = await api.analytics.summary(scope, scopeId, { ignoreAnnea });
      setApps(data.apps || []);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('[Analytics] Failed to load summary', error);
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  const loadChartEvents = async () => {
    setChartLoading(true);
    try {
      const data = await api.analytics.listEvents({
        days: 30,
        limit: 10000,
        organizationId: selectedOrgId || undefined,
        userId: selectedUserId || undefined,
        ignoreAnnea,
      });
      setChartEvents(data.events || []);
    } catch (error) {
      console.error('[Analytics] Failed to load chart events', error);
      setChartEvents([]);
    } finally {
      setChartLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    await Promise.all([loadSummary(), loadChartEvents()]);
  };

  const totals = useMemo(() => {
    return apps.reduce(
      (acc, app) => {
        acc.lastDay += app.lastDay || 0;
        acc.yesterday += app.yesterday || 0;
        acc.lastWeek += app.lastWeek || 0;
        acc.last28Days += app.last28Days || 0;
        acc.total += app.total || 0;
        return acc;
      },
      { lastDay: 0, yesterday: 0, lastWeek: 0, last28Days: 0, total: 0 }
    );
  }, [apps]);
  const dailyEvents = useMemo(() => buildLast30EventDays(chartEvents), [chartEvents]);

  const columns: ColumnsType<AnalyticsSummaryItem> = [
    {
      title: 'App',
      dataIndex: 'appName',
      key: 'appName',
      render: (_value, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.appName}</div>
          <Text type="secondary">{record.appSlug}</Text>
        </div>
      ),
    },
    {
      title: 'Today',
      dataIndex: 'lastDay',
      key: 'lastDay',
      width: 120,
    },
    {
      title: 'Yesterday',
      dataIndex: 'yesterday',
      key: 'yesterday',
      width: 130,
    },
    {
      title: 'Last 7 Days',
      dataIndex: 'lastWeek',
      key: 'lastWeek',
      width: 140,
    },
    {
      title: 'Last 28 Days',
      dataIndex: 'last28Days',
      key: 'last28Days',
      width: 150,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Usage Analytics</Title>
          <Text type="secondary">
            Summary across all apps{lastUpdated ? ` · Updated ${lastUpdated}` : ''}
          </Text>
        </div>
        <Space>
          <Text type="secondary">Auto refresh</Text>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          <Button onClick={() => void refreshAnalytics()} loading={loading || chartLoading}>
            Refresh
          </Button>
        </Space>
      </div>

      <AnalyticsFilters
        ignoreAnnea={ignoreAnnea}
        organizationId={selectedOrgId}
        userId={selectedUserId}
        onIgnoreAnneaChange={setIgnoreAnnea}
        onOrganizationChange={(value) => {
          setSelectedOrgId(value);
          setSelectedUserId(null);
        }}
        onUserChange={setSelectedUserId}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Today" value={totals.lastDay} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Yesterday" value={totals.yesterday} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Last 7 Days" value={totals.lastWeek} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Last 28 Days" value={totals.last28Days} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Total" value={totals.total} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <ColumnChart
            title={chartLoading ? 'Events per Day (Last 30 Days) - Loading' : 'Events per Day (Last 30 Days)'}
            items={dailyEvents}
          />
        </Col>
      </Row>

      <Card>
        <Table
          rowKey={(record) => record.appId || record.appSlug}
          columns={columns}
          dataSource={apps}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No analytics data yet.' }}
        />
      </Card>
    </div>
  );
}

